<template>
  <div class="px-2 pt-3">
    <div>
      <h3 class="d-inline-block py-3">{{label}}</h3>
      <v-btn v-if="!readonly" color="pink" dark small fab @click="addItem">
        <v-icon>add</v-icon>
      </v-btn>
    </div>
    <!-- <code>defaultForm: {{defaultForm}}</code>
    <code>items: {{items}}</code>
    <code>defaultFormMap: {{defaultFormMap}}</code> -->
    <!-- <code> {{tableItems.length}} {{defaultForm.length}} </code> -->
    <div
      v-for="(item, i) in tableItems"
      :key="item._id"
    >
      <template v-if="isPrimitiveSchema">
        <v-text-field
          v-model="item.value"
          :type="primitiveInputType"
          :label="label + ' #' + (getRealItemIndex(i) + 1)"
          :readonly="readonly"
          class="mb-2"
          @input="onPrimitiveInput"
        >
          <template v-slot:append>
            <v-btn
              v-if="!readonly"
              flat icon class="btn-toolbar"
              @click.stop.prevent="removeItem(item)">
              <v-icon>delete</v-icon>
            </v-btn>
          </template>
        </v-text-field>
      </template>
      <template v-else>
        <MongoFields
          ref="mongoFields"
          v-model="item.value"
          :defaultForm="defaultFormMap[item._id]"
          :title="label"
          :schema="schema"
          :mongooseSchema="mongooseSchema"
          :formRules="formRules.seek(getRealItemIndex(i))"
          :rowClasses="rowClasses"
          :expand="expand"
          :flat="flat"
          :readonly="readonly"
        >
          <template slot="header">
            <div>
              <h4 class="d-inline-block">Item ({{ label }})</h4>
              <v-btn
                v-if="!readonly"
                flat icon class="btn-toolbar"
                @click.stop.prevent="removeItem(item)">
                <v-icon>delete</v-icon>
              </v-btn>
            </div>
          </template>
        </MongoFields>
      </template>
    </div>
    <v-btn v-if="maxItem < items.length" @click="maxItem += itemPerPage" darken color="primary" class="ml-0">
      <v-icon style="margin-left: -12px">expand_more</v-icon>
      Show more {{items.length - maxItem < itemPerPage ? items.length - maxItem : itemPerPage}} items
      ({{items.length - maxItem}} items left)
    </v-btn>
  </div>
</template>

<script>
const MongoFields = () => import(/* webpackChunkName: "main" */ './MongoFields.vue')
import { createMongooseSchema } from '../../services/mongo-connector.service.js'
import { getUniqueId } from '../../libs/utils.js'
import { MongoTableRules, createDocumentId } from '../../libs/mongo-datatable'

function isPrimitiveType(type) {
  return type === String || type === Number || type === Boolean
}

export default {
  name: 'MongoArrayItems',
  props: {
    value: { type: Array },
    // Passed props
    defaultForm: { required: false, type: [Object, Array], default: () => ([]) },
    propSchema: { required: true, type: Object },
    formRules: { type: MongoTableRules, required: false, default: () => new MongoTableRules() },
    label: { required: false, default: '' },
    rowClasses: { type: String, required: false },
    expand: { type: Boolean, required: false, default: false },
    flat: { type: Boolean, required: false, default: false },
    readonly: { type: Boolean, required: false, default: false }
  },
  data () {
    return {
      CUSTOM_FIELD: true, // Used in MongoField
      maxItem: 5,
      itemPerPage: 5,
      items: [],
      defaultFormMap: {}
    }
  },
  computed: {
    schema () {
      const schema = this.propSchema.type[0] || {}
      return schema
    },
    isPrimitiveSchema() {
      return isPrimitiveType(this.propSchema.type[0])
    },
    primitiveInputType() {
      if (this.propSchema.type[0] === Number) return 'number'
      if (this.propSchema.type[0] === Boolean) return 'checkbox'
      return 'text'
    },
    mongooseSchema () {
      return createMongooseSchema(this.schema)
    },
    orderedItems () {
      return [...this.items].reverse()
    },
    tableItems () {
      return this.orderedItems.slice(0, this.maxItem)
    }
  },
  watch: {
    defaultForm: {
      deep: true,
      handler (v) {
        this.reset()
      }
    },
    items: {
      deep: true,
      handler (val) {
        let result = val.map(v => v.value)
        if (this.isPrimitiveSchema && this.primitiveInputType === 'number') {
          result = result.map(v => v === null || v === undefined || v === '' ? null : Number(v))
        }
        this.$emit('input', result)
      }
    }
  },
  mounted () {
    this.init()
  },
  methods: {
    reset () {
      this.maxItem = this.itemPerPage
      if (Array.isArray(this.$refs.mongoFields)) {
        this.$refs.mongoFields.forEach(component => {
          component.reset()
        })
      }
      this.$nextTick(() => this.init())
    },
    init () {
      console.log('MongoArrayItems reading configuration (defaultForm):', this.defaultForm)
      const defaultForm = Array.isArray(this.defaultForm) ? this.defaultForm : []

      if (this.isPrimitiveSchema) {
        this.items = defaultForm.map(val => {
          let value = val
          if (this.primitiveInputType === 'number') {
            value = (val === null || val === undefined || val === '') ? null : Number(val)
          }
          return { _id: getUniqueId(), value }
        })
        this.defaultFormMap = {}
      } else {
        for (const item of defaultForm) {
          if (item && typeof item === 'object' && !item._id) {
            item._id = createDocumentId()
          }
        }
        this.defaultFormMap = defaultForm.reduce((c, v) => {
          if (v && typeof v === 'object' && v._id) {
            c[v._id] = v
          }
          return c
        }, {})
        this.items = defaultForm.map(v => {
          if (v && typeof v === 'object') {
            return { _id: v._id, value: v }
          }
          return null
        }).filter(Boolean)
      }
    },
    addItem () {
      this.maxItem += 1
      this.items.push({
        _id: getUniqueId(),
        value: this.isPrimitiveSchema ? null : {}
      })
    },
    removeItem (item) {
      this.maxItem -= 1
      const index = this.items.indexOf(item)
      this.items.splice(index, 1)
    },
    getRealItemIndex (index) {
      return this.orderedItems.length - index - 1
    },
    onPrimitiveInput() {
      if (this.isPrimitiveSchema && this.primitiveInputType === 'number') {
        this.$emit('input', this.items.map(v => v.value === null || v.value === undefined || v.value === '' ? null : Number(v.value)))
      } else {
        this.$emit('input', this.items.map(v => v.value))
      }
    }
  },
  components: {
    MongoFields
  }
}
</script>
